<template>
    <div class="career-container">
        <!-- Hero Section -->
        <section class="hero">
            <div class="hero-content">
                <p class="hero-subtitle">Professional Journey & Experience</p>
                <div class="hero-badges">
                    <span class="badge">Research & Development</span>
                    <span class="badge">Backend Development</span>
                    <span class="badge">Generative AI</span>
                </div>
            </div>
        </section>

        <!-- Main Content -->
        <div class="content-wrapper">
            <!-- Current Position -->
            <section id="current" class="section">
                <div class="section-header">
                    <h2 class="section-title">Current Position</h2>
                    <div class="section-line"></div>
                </div>
                <div class="timeline-item current">
                    <div class="timeline-marker current-marker"></div>
                    <div class="timeline-content">
                        <div class="position-header">
                            <h3 class="position-title">GenAI Trainee</h3>
                            <span class="company">Santander Bank, Lisbon</span>
                            <span class="period">2025 - Present</span>
                        </div>
                        <div class="position-description">
                            <p class="text-content">
                                Currently working as a GenAI Trainee at Santander Bank in Lisbon, Portugal.
                                Focusing on implementing solutions that leverage Generative AI to increase performance
                                internally and improve customer experience.
                            </p>
                            <div class="achievements">
                                <h4 class="achievements-title">Key Focus Areas:</h4>
                                <ul class="achievements-list">
                                    <li>Generative AI</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Professional Experience -->
            <section id="experience" class="section">
                <div class="section-header">
                    <h2 class="section-title">Professional Experience</h2>
                    <div class="section-line"></div>
                </div>
                <div class="timeline">
                    <div class="timeline-item">
                        <div class="timeline-marker"></div>
                        <div class="timeline-content">
                            <div class="position-header">
                                <h3 class="position-title">Research Intern</h3>
                                <span class="company">National Institute of Informatics, Tokyo</span>
                                <span class="period">2025</span>
                            </div>
                            <div class="position-description">
                                <p class="text-content">
                                    Currently working as a Research Intern at the National Institute of Informatics in Tokyo, Japan. 
                                    Researching the use of Large Language Models for Formal Methods, in particular to generate
                                    missing predicates in in Event-B models.
                                </p>
                                <div class="achievements">
                                    <h4 class="achievements-title">Key Focus Areas:</h4>
                                    <ul class="achievements-list">
                                        <li>Formal Methods</li>
                                        <li>Generative AI</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-item offset-marker">
                        <div class="timeline-marker"></div>
                        <div class="timeline-content">
                            <div class="position-header">
                                <h3 class="position-title">Research Grant Owner & Backend Developer</h3>
                                <span class="company">DSI - Técnico's IT Services</span>
                                <span class="period">January 2023 - March 2025</span>
                            </div>
                            <div class="position-description">
                                <p class="text-content">
                                    Worked as a research grant owner at Instituto Superior Técnico's IT services department, 
                                    contributing primarily as a backend developer on institutional projects.
                                </p>
                                <div class="achievements">
                                    <h4 class="achievements-title">Key Contributions:</h4>
                                    <ul class="achievements-list">
                                        <li>Contributor to <a href="https://github.com/ist-dsi/tecnico-api" class="link" target="_blank">Técnico API</a> development</li>
                                        <li>Contributions for other projects not open source</li>
                                    </ul>
                                </div>
                                <div class="tech-stack">
                                    <h4 class="tech-title">Technologies Used:</h4>
                                    <div class="tech-tags">
                                        <span class="tech-tag">Java</span>
                                        <span class="tech-tag">Spring Framework</span>
                                        <span class="tech-tag">Maven</span>
                                        <span class="tech-tag">REST APIs</span>
                                        <span class="tech-tag">Git</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="timeline">
                    <div class="timeline-item big-offset-marker">
                        <div class="timeline-marker"></div>
                        <div class="timeline-content">
                            <div class="position-header">
                                <h3 class="position-title">Afterschool Course Instructor</h3>
                                <span class="company">TreeTree2</span>
                                <span class="period">October 2022 - Novemeber 2022</span>
                            </div>
                            <div class="position-description">
                                <p class="text-content">
                                    Voluntary work as an afterschool course instructor at TreeTree2,
                                    giving classes on Python programming to children aged 12 to 17.
                                </p>
                                <div class="tech-stack">
                                    <h4 class="tech-title">Technologies Used:</h4>
                                    <div class="tech-tags">
                                        <span class="tech-tag">Python</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Academic Involvement -->
            <section id="involvement" class="section">
                <div class="section-header">
                    <h2 class="section-title">Academic Involvement</h2>
                    <div class="section-line"></div>
                </div>
                <div class="involvement-grid">
                    <div class="involvement-card">
                        <div class="involvement-icon">🎮</div>
                        <h3 class="involvement-title">GameDevTécnico</h3>
                        <span class="involvement-role">Active Member</span>
                        <p class="involvement-description">
                            Member of the GameDevTécnico student group at Instituto Superior Técnico, 
                            participating in game development internal projects and game jams.
                        </p>
                        <div class="involvement-activities">
                            <span class="activity-tag">Game Development</span>
                            <span class="activity-tag">Project Collaboration</span>
                        </div>
                    </div>
                    <div class="involvement-card">
                        <div class="involvement-icon">🎮</div>
                        <h3 class="involvement-title">LabJogos</h3>
                        <span class="involvement-role">Former Member</span>
                        <p class="involvement-description">
                            Member of the LabJogos student group at Instituto Superior Técnico, 
                            helping with the organization of game development events and workshops.
                        </p>
                        <div class="involvement-activities">
                            <span class="activity-tag">Event Organization</span>
                            
                        </div>
                    </div>
                </div>
            </section>

            <!-- Skills & Expertise -->
            <section id="expertise" class="section">
                <div class="section-header">
                    <h2 class="section-title">Professional Skills</h2>
                    <div class="section-line"></div>
                </div>
                <div class="expertise-grid">
                    <div class="expertise-card">
                        <div class="expertise-icon">🔬</div>
                        <h3 class="expertise-title">Research & Development</h3>
                        <div class="expertise-skills">
                            <span class="skill-tag">Formal Methods</span>
                            <span class="skill-tag">Generative AI</span>
                        </div>
                    </div>
                    
                    <div class="expertise-card">
                        <div class="expertise-icon">⚙️</div>
                        <h3 class="expertise-title">Backend Development</h3>
                        <div class="expertise-skills">
                            <span class="skill-tag">Java</span>
                            <span class="skill-tag">Spring Framework</span>
                            <span class="skill-tag">API Design</span>
                        </div>
                    </div>
                    
                    <div class="expertise-card">
                        <div class="expertise-icon">🤖</div>
                        <h3 class="expertise-title">AI & Technology</h3>
                        <div class="expertise-skills">
                            <span class="skill-tag">Machine Learning</span>
                            <span class="skill-tag">OpenAI API</span>
                            <span class="skill-tag">Autonomous Agents</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CareerView'
}
</script>

<style lang="scss" scoped>
// Using the same CSS variables as the main app
:root {
    --primary-color: #00d4ff;
    --primary-gradient: linear-gradient(135deg, #00d4ff 0%, #5b73ff 100%);
    --accent-color: #ff6b6b;
    --accent-gradient: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%);
    --secondary-color: #1a1d23;
    --background-dark: #0f1419;
    --background-darker: #0a0e13;
    --background-card: #1a1d23;
    --text-light: #e6e6e6;
    --text-bright: #ffffff;
    --text-muted: #8892b0;
    --text-dim: #64748b;
    --border-color: #2d3748;
    --border-glow: rgba(0, 212, 255, 0.2);
    --shadow-dark: 0 4px 20px rgba(0, 0, 0, 0.4);
    --shadow-hover: 0 8px 30px rgba(0, 0, 0, 0.6);
    --shadow-glow: 0 0 20px rgba(0, 212, 255, 0.1);
}

.career-container {
    min-height: 100vh;
    color: var(--text-light);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.6;
}

// Hero Section
.hero {
    text-align: center;
    padding: 2rem 0;
    margin-bottom: 2rem;
}

.hero-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 2rem;
}

.hero-subtitle {
    font-size: 1.5rem;
    font-weight: 300;
    margin-bottom: 2rem;
    color: var(--text-muted);
    animation: fadeInUp 1s ease-out;
}

.hero-badges {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    animation: fadeInUp 1s ease-out 0.2s both;
}

.badge {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(91, 115, 255, 0.1) 100%);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 212, 255, 0.3);
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-bright);
    transition: all 0.3s ease;
    
    &:hover {
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(91, 115, 255, 0.2) 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
    }
}

// Content Wrapper
.content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
}

// Section Styles
.section {
    margin-bottom: 4rem;
    animation: fadeInUp 0.8s ease-out;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;
}

.section-line {
    width: 60px;
    height: 4px;
    background: var(--primary-gradient);
    margin: 0 auto;
    border-radius: 2px;
}

// Timeline Styles
.timeline {
    position: relative;
    padding-left: 2rem;
    
    &::before {
        content: '';
        position: absolute;
        left: 1rem;
        top: 0;
        bottom: 0;
        width: 2px;
        background: var(--primary-gradient);
    }
}

.timeline-item {
    position: relative;
    margin-bottom: 3rem;
    
    &.current {
        .timeline-content {
            background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, rgba(91, 115, 255, 0.05) 100%);
            border: 2px solid var(--primary-color);
        }
    }
}

.timeline-item.offset-marker .timeline-marker {
    top: 4rem;
}

.timeline-item.big-offset-marker .timeline-marker {
    top: 8rem;
}

.timeline-marker {
    position: absolute;
    left: -1.5rem;
    top: 1rem;
    width: 1rem;
    height: 1rem;
    background: var(--primary-color);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
    
    &.current-marker {
        width: 1.2rem;
        height: 1.2rem;
        left: -1.6rem;
        background: var(--primary-gradient);
        animation: pulse 2s infinite;
    }
}

.timeline-content {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    margin-left: 1rem;
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-2px);
        border-color: var(--border-glow);
    }
}

.position-header {
    margin-bottom: 1.5rem;
    
    .position-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--text-bright);
        margin-bottom: 0.5rem;
    }
    
    .company {
        display: block;
        font-size: 1.1rem;
        color: var(--primary-color);
        font-weight: 600;
        margin-bottom: 0.5rem;
    }
    
    .period {
        display: inline-block;
        background: var(--accent-gradient);
        color: white;
        padding: 0.3rem 0.8rem;
        border-radius: 15px;
        font-size: 0.85rem;
        font-weight: 500;
    }
}

.position-description {
    .text-content {
        font-size: 1rem;
        line-height: 1.7;
        margin-bottom: 1.5rem;
        color: var(--text-light);
    }
}

.achievements, .tech-stack {
    margin-top: 1.5rem;
    
    .achievements-title, .tech-title {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-bright);
        margin-bottom: 1rem;
    }
}

.achievements-list {
    list-style: none;
    padding: 0;
    
    li {
        position: relative;
        padding-left: 1.5rem;
        margin-bottom: 0.5rem;
        color: var(--text-light);
        
        &::before {
            content: '▶';
            position: absolute;
            left: 0;
            color: var(--primary-color);
            font-size: 0.8rem;
        }
    }
}

.tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.tech-tag {
    background: var(--secondary-color);
    color: var(--text-bright);
    padding: 0.4rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
    border: 1px solid var(--border-color);
    transition: all 0.3s ease;
    
    &:hover {
        background: var(--primary-color);
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 212, 255, 0.3);
    }
}

// Involvement Grid
.involvement-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 2rem;
}

.involvement-card {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    text-align: center;
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-4px);
        border-color: var(--border-glow);
    }
}

.involvement-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    display: block;
}

.involvement-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--text-bright);
    margin-bottom: 0.5rem;
}

.involvement-role {
    color: var(--primary-color);
    font-weight: 500;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    display: block;
}

.involvement-description {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 1.5rem;
}

.involvement-activities {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.activity-tag {
    background: var(--accent-gradient);
    color: white;
    padding: 0.3rem 0.6rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
}

// Expertise Grid
.expertise-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
}

.expertise-card {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    text-align: center;
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-4px);
        border-color: var(--border-glow);
    }
}

.expertise-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    display: block;
}

.expertise-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--text-bright);
    margin-bottom: 1.5rem;
}

.expertise-skills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.skill-tag {
    background: var(--primary-gradient);
    color: white;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
    }
}

// Links
.link {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
        color: #5b73ff;
    }
}

// Animations
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes pulse {
    0%, 100% {
        box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
    }
    50% {
        box-shadow: 0 0 20px rgba(0, 212, 255, 0.8);
    }
}

// Responsive Design
@media (max-width: 768px) {
    .hero-subtitle {
        font-size: 1.2rem;
    }
    
    .content-wrapper {
        padding: 1.5rem;
    }
    
    .timeline {
        padding-left: 1rem;
        
        &::before {
            left: 0.5rem;
        }
    }
    
    .timeline-marker {
        left: -1rem;
        
        &.current-marker {
            left: -1.1rem;
        }
    }
    
    .timeline-content {
        margin-left: 0.5rem;
        padding: 1.5rem;
    }
    
    .section-title {
        font-size: 2rem;
    }
    
    .involvement-grid, .expertise-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .hero-subtitle {
        font-size: 1rem;
    }
    
    .hero-badges {
        flex-direction: column;
        align-items: center;
    }
    
    .content-wrapper {
        padding: 1rem;
    }
    
    .timeline-content {
        padding: 1rem;
    }
    
    .section-title {
        font-size: 1.5rem;
    }
    
    .involvement-card, .expertise-card {
        padding: 1.5rem;
    }
}
</style>